import React, { useContext, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { SortOrder } from 'primereact/api';
import { getEntities } from 'app/api/generalApi';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ToastContext } from 'app/context/ToastContext';
import './styles.css';
import {
  acceptProvisionSheet,
  downloadProvisionSheetById,
  generateProvisionSheets,
  getLatestProvisionSheetGroup,
  handleDownloadExcelById,
  prepareForVyplatenieProvisionSheet,
  regenerateProvisionSheets,
  vyplatitPripraveneProvizie,
} from 'app/api/provisionSheetApi';
import { formatFromLocalDate } from 'app/shared/util/date-utils';
import { Checkbox } from 'primereact/checkbox';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';

const ProvisionSheetTable: React.FC = () => {
  const navigate = useNavigate();

  const { showSuccess, showError } = useContext(ToastContext);

  const [loading, setLoading] = useState(true);
  const [provisions, setProvisions] = useState([]);
  const [entity, setEntity] = useState(null);
  const [deleteContactDialog, setDeleteContactDialog] = useState(false);
  const toast = useRef(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [latestSheetGroupId, setLatestSheetGroupId] = useState(null);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 20,
    page: 0,
    sortField: 'id',
    sortOrder: SortOrder.DESC,
    filter: '',
  });

  const refreshLatestProvisionSheetGroup = () => {
    if (isAdmin) {
      getLatestProvisionSheetGroup()
        .then(response => {
          setLatestSheetGroupId(response.data);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba pri načítaní poslednej províznej skupiny', e.response?.data?.detail);
        });
    }
  };

  useEffect(() => {
    refreshLatestProvisionSheetGroup();
  }, []);

  useEffect(() => {
    loadData();
  }, [lazyParams]);

  const loadData = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      ...(lazyParams.filter ? { query: lazyParams.filter } : {}),
    };

    getEntities('/api/provision-sheets', requestData)
      .then(response => {
        setProvisions(response.data.content);
        setTotalRecords(response.data.totalElements);
      })
      .catch(() => {
        showError('Chyba', 'Nastala chyba pri načítaní výkazov provízií.');
      })
      .finally(() => setLoading(false));
  };

  const onPage = event => {
    setLazyParams({ ...event, filter: filterValue });
  };

  const onSort = event => {
    setLazyParams({ ...event, page: lazyParams.page, filter: filterValue });
  };

  const handleRegenerate = () => {
    setLoading(true);
    setDeleteContactDialog(false);

    regenerateProvisionSheets()
      .then(r => {
        refreshLatestProvisionSheetGroup();
        setProvisions(r.data.content);
        showSuccess('Úspech', 'Posledné provízne listy sa vymazali a vygenerovali sa nanovo.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleGenerate = () => {
    setLoading(true);

    generateProvisionSheets()
      .then(r => {
        setProvisions(r.data.content);
        showSuccess('Úspech', 'Provízne listy boli úspešne vygenerované.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleVyplatitProvizie = () => {
    setLoading(true);

    vyplatitPripraveneProvizie()
      .then(r => {
        // setProvisions(r.data.content);
        showSuccess('Úspech', 'Provízne listy boli úspešne vyplatené.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleAccept = id => {
    acceptProvisionSheet(id)
      .then(r => {
        showSuccess('Úspech', 'Provízne listy bol akceptovný.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const handlePrepareForVyplatenie = id => {
    prepareForVyplatenieProvisionSheet(id)
      .then(r => {
        showSuccess('Úspech', 'Provízny list bol pripravený na vyplatenie.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const handleDownload = id => {
    downloadProvisionSheetById(id)
      .then(r => {})
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const handleDownloadExcel = id => {
    handleDownloadExcelById(id)
      .then(r => {})
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const confirmDeleteContact = contactToDelete => {
    // setProduct(product);
    setEntity(contactToDelete);
    setDeleteContactDialog(true);
  };

  const hideDeleteContactDialog = () => {
    setDeleteContactDialog(false);
  };

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          <Button
            icon="pi pi-download"
            tooltip="Stiahnuť provízny list"
            rounded
            outlined
            className="mr-2"
            onClick={() => handleDownload(rowData.id)}
          />
          {rowData.vyplatene && isAdmin && (
            <Button
              icon="pi pi-file-excel"
              tooltip="Stiahnuť vygenerovaný excel so zoznamom vyplatených provízií"
              rounded
              outlined
              className="mr-2"
              onClick={() => handleDownloadExcel(rowData.id)}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={hideDeleteContactDialog} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={handleRegenerate} />
    </React.Fragment>
  );

  const renderHeader = () => {
    return (
      isAdmin && (
        <div className="flex justify-content-between">
          <Button label="Pregenerovať posledné hárky znovu" severity="danger" onClick={() => setDeleteContactDialog(true)} />

          <Button label="Manuálne vygenerovať provízne hárky" severity="warning" onClick={handleGenerate} />
          <Button label="Vyplatiť pripravené provízie" severity="info" onClick={handleVyplatitProvizie} />
        </div>
      )
    );
  };

  const nameBodyTemplate = rowData => {
    if (rowData.agent?.name) {
      return rowData.agent?.name;
    }

    if (rowData.agent?.firstName) {
      return rowData.agent?.firstName + ' ' + rowData.agent?.lastName;
    }

    return '';
  };

  const onCheckboxChange = id => {
    setProvisions(prev => prev.map(p => (p.id === id ? { ...p, accepted: !p.accepted } : p)));
    handleAccept(id);
  };

  const onCheckboxPrepareForVyplatenieChange = id => {
    setProvisions(prev => prev.map(p => (p.id === id ? { ...p, prepareForVyplatenie: !p.prepareForVyplatenie } : p)));
    handlePrepareForVyplatenie(id);
  };

  const acceptedBodyTemplate = rowData => {
    return (
      <span>
        <Checkbox
          disabled={rowData.vyplatene}
          onChange={event => onCheckboxChange(rowData.id)}
          checked={rowData.accepted as boolean}
        ></Checkbox>
      </span>
    );
  };

  const prepareForVyplatenieBodyTemplate = rowData => {
    return (
      <span>
        <Checkbox
          disabled={rowData.vyplatene}
          onChange={event => onCheckboxPrepareForVyplatenieChange(rowData.id)}
          checked={rowData.prepareForVyplatenie as boolean}
        ></Checkbox>
      </span>
    );
  };

  const vyplateneBodyTemplate = rowData => {
    return (
      <span>
        <Checkbox onChange={null} disabled={true} checked={rowData.vyplatene as boolean}></Checkbox>
      </span>
    );
  };

  const isProcessedIcon = processed => {
    return (
      <span>
        {processed ? (
          <i className="pi pi-check" style={{ color: 'green', fontWeight: 'bold' }}></i>
        ) : (
          <i className="pi pi-times" style={{ color: 'red', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const header = renderHeader();

  const rowClassName = data => {
    if (data?.provisionSheetGroup?.id === latestSheetGroupId) {
      return 'row-color-latest';
    }

    return ``;
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="card">
        <DataTable
          stripedRows
          value={provisions}
          size="small"
          lazy
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne dáta."
          header={header}
          paginator
          first={lazyParams.first}
          rows={20}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          rowClassName={rowClassName}
          // rowExpansionTemplate={expandTemplate}
          // expandedRows={expandedRows}
          // onRowToggle={e => setExpandedRows(e.data)}
          // onRowExpand={onRowExpand}
          // onRowCollapse={onRowCollapse}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          filterDisplay="menu"
          loading={loading}
        >
          {/*<Column expander={allowExpansion} style={{ width: '5rem' }} />*/}
          <Column field="id" header="ID" sortable={true} />
          {isAdmin && <Column field="accepted" header="Schválené" sortable={true} body={v => acceptedBodyTemplate(v)} />}
          {isAdmin && (
            <Column
              field="prepareForVyplatenie"
              header="Pripravené na vyplatenie"
              sortable={true}
              body={v => prepareForVyplatenieBodyTemplate(v)}
            />
          )}
          {isAdmin && <Column field="vyplatene" header="Vyplatené" sortable={true} body={v => vyplateneBodyTemplate(v)} />}
          <Column field="agent" header="Poradca" body={v => nameBodyTemplate(v)} />
          <Column field="setFrom" header="Od" sortable={true} body={v => formatFromLocalDate(v.from)} />
          <Column field="setTo" header="Do" sortable={true} body={v => formatFromLocalDate(v.to)} />
          <Column field="taxDocumentNr" sortable={true} header="Daňové číslo" />
          <Column
            field="celkomNaVyplatu"
            sortable={true}
            header="Celkom na výplatu"
            body={v => (v.celkomNaVyplatu ? String(v.celkomNaVyplatu).replace('.', ',') + ' €' : '0,00 €')}
          />
          <Column body={actionBodyTemplate} style={{ textAlign: 'center' }}></Column>
        </DataTable>
      </div>
      <Dialog
        visible={deleteContactDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteProductDialogFooter}
        onHide={hideDeleteContactDialog}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Naozaj chcete pregenerovať posledne vygenerované provízne hárky? Hárky budú najskôr vymazané.</span>
        </div>
      </Dialog>
    </div>
  );
};

export default ProvisionSheetTable;
